import React, { useEffect } from 'react';
import { Button, Form, Rate, Skeleton } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useNavigate, useParams } from 'react-router-dom';
import { TwoColumnsLayout } from 'common/components/Layouts/TwoColumnsLayout';
import { MobileLayout } from 'common/components/Layouts/MobileLayout';
import { rateStoreValidationRules } from 'common/consts/validation.consts';
import { ERoutesCommon } from 'common/models/routesModel';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getOrderHistoryModel, setOrderHistoryModel } from 'app/store/reducers/orderHistory.reducer';
import { addRatingsModel } from 'app/store/reducers/rating.reducer';
import { RatingsHooks } from 'entities/Ratings/Ratings.hooks';

interface IFormValues {
  rate: number;
  quality: number;
  serve: number;
  friendly: number;
  feedback?: string;
}

export const RateStorePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { customerModel } = useAppSelector((state) => state.customer);
  const { ratingModelLoading } = useAppSelector((state) => state.rating);
  const { orderHistoryModel, orderHistoryModelLoading } = useAppSelector((state) => state.orderHistory);
  const { id: orderId } = useParams<{ id: string }>();
  const { store } = orderHistoryModel || {};
  const storeName = store?.storeName;
  const header = orderHistoryModelLoading ? (
    <Skeleton.Input active style={{ width: '320px' }} />
  ) : (
    `How was your order at ${storeName || 'store'} today?`
  );

  const clearOrderHistory = () => {
    dispatch(setOrderHistoryModel(null));
  };

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderHistoryModel(orderId));
    }

    return () => clearOrderHistory();
  }, [orderId]);

  RatingsHooks.useRedirectIfError(orderHistoryModel, customerModel);

  const submit = (values: IFormValues) => {
    const { feedback, rate, friendly, quality, serve } = values;

    if (orderId) {
      dispatch(addRatingsModel({ feedback, rate, friendly, quality, serve, orderHistory: orderId }));
    }
  };

  const cancel = () => navigate(ERoutesCommon.Main);

  return (
    <TwoColumnsLayout
      left={
        <MobileLayout header={header}>
          <Form onFinish={submit} layout="vertical">
            <Form.Item
              name="rate"
              rules={rateStoreValidationRules.rate}
              label={
                <span>
                  Please rate your <b>overall</b> experience from 1-5
                </span>
              }
            >
              <Rate allowClear={false} className="rate-store-stars" />
            </Form.Item>
            <Form.Item name="quality" rules={rateStoreValidationRules.quality} label="How was the quality of your food today?">
              <Rate allowClear={false} className="rate-store-stars" />
            </Form.Item>
            <Form.Item name="serve" rules={rateStoreValidationRules.serve} label="How fast did we serve you today?">
              <Rate allowClear={false} className="rate-store-stars" />
            </Form.Item>
            <Form.Item
              name="friendly"
              rules={rateStoreValidationRules.friendly}
              label="How friendly was our crew's service today?"
            >
              <Rate allowClear={false} className="rate-store-stars" />
            </Form.Item>
            <Form.Item className="mb-9" name="feedback" rules={rateStoreValidationRules.feedback} label="Your feedback">
              <TextArea name="feedback" rows={6} placeholder="What would you like the store to know? (240 ch. max)" />
            </Form.Item>
            <Form.Item>
              <Button className="btn" block type="primary" htmlType="submit" disabled={ratingModelLoading}>
                Submit
              </Button>
            </Form.Item>
            <Form.Item>
              <Button className="btn" block onClick={cancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </MobileLayout>
      }
    />
  );
};
